import { AxiosError } from 'axios';
import { ICreator } from '~/types';

import { useGetCreatorBenchmarks } from './useGetCreatorBenchmarks';
import { useGetCreatorIGInsights } from './useGetCreatorIGInsights';
import { useGetCreatorTTInsights } from './useGetCreatorTTInsights';
import { useGetCreatorYTInsights } from './useGetCreatorYTInsights';

const defaultEmptyInsights = {
  ages: [],
  agesShort: [],
  cities: null,
  countries: [],
  genders: [],
  gendersPerAge: [],
};

export const useGetCreatorInsightsAndBenchmarks = (
  creator: ICreator | null,
) => {
  const creatorId = creator?.id;
  const benchmarkId = creator?.benchmark?.id;
  const igInsightId = creator?.insight?.id;
  const ttInsightId = creator?.tiktokInsight?.id;
  const ytChannelId = creator?.ytChannels?.[0]?.id;

  const {
    data: benchmarksData,
    isFetched: isFetchedBenchmarks,
    isFetching: isFetchingBenchmarks,
    error: errorBenchmarks,
  } = useGetCreatorBenchmarks(benchmarkId);
  const {
    data: igInsightsData,
    isFetched: isFetchedIGInsights,
    isFetching: isFetchingIGInsights,
    error: errorIGInsights,
  } = useGetCreatorIGInsights(igInsightId);
  const {
    data: ttInsightsData,
    isFetched: isFetchedTTInsights,
    isFetching: isFetchingTTInsights,
    error: errorTTInsights,
  } = useGetCreatorTTInsights(ttInsightId);
  const {
    data: ytInsightsData,
    isFetched: isFetchedYTInsights,
    isFetching: isFetchingYTInsights,
    error: errorYTInsights,
  } = useGetCreatorYTInsights(ytChannelId);

  const areBenchmarkReady = Boolean(
    !benchmarkId || (benchmarkId && isFetchedBenchmarks),
  );
  const areInsightsReady =
    Boolean(!igInsightId || (igInsightId && isFetchedIGInsights)) &&
    Boolean(!ttInsightId || (ttInsightId && isFetchedTTInsights)) &&
    Boolean(!ytChannelId || (ytChannelId && isFetchedYTInsights));
  const areMetricsReady =
    Boolean(benchmarkId || igInsightId || ttInsightId || ytChannelId) &&
    areBenchmarkReady &&
    areInsightsReady;
  // Return isError=true only if all requests fails (except with a 404 code)
  const isError = [
    errorBenchmarks,
    errorIGInsights,
    errorTTInsights,
    errorYTInsights,
  ].every((error: AxiosError) => error && error?.response?.status !== 404);

  // Do not return data until all metrics are fetched
  if (!areMetricsReady || isError)
    return {
      benchmarks: null,
      insights: null,
      isFetching:
        isFetchingBenchmarks ||
        isFetchingIGInsights ||
        isFetchingTTInsights ||
        isFetchingYTInsights,
      isError,
    };

  return {
    isFetching: false,
    isError: false,
    benchmarks: {
      id: creatorId,
      benchmarks: benchmarksData?.data,
    },
    insights: {
      id: creatorId,
      insights: {
        instagram: igInsightsData?.data || defaultEmptyInsights,
        tiktok: ttInsightsData?.data || defaultEmptyInsights,
        youtube: ytInsightsData?.data || defaultEmptyInsights,
      },
    },
  };
};
